/**
 * PillarTabs — sticky pill tab bar shared by all three Posture pages.
 *
 * Generic over a `TabId` string-union so each page passes its own tab set
 * (e.g. `'drifts' | 'pr-tracker' | 'export'` for Baseline Compliance,
 * `'overview' | 'mfa' | 'licenses' | 'inactive' | 'all'` for User Analytics).
 *
 * URL-syncs the active tab via a configurable search param (default: `tab`).
 * The default tab elides from the URL to keep canonical links clean.
 *
 * Visual: matches the `.tabs` block from the redesign mockups —
 * uppercase pill chips, JetBrains-Mono numeric badges, navy active fill,
 * tone-tinted active badge background.
 *
 * Reference pattern: `Dashboard/v2/PostureTabs.tsx` (Command Center). This is
 * the parameterized version. PostureTabs stays in place for the dashboard.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import type { PillarTone } from '@/components/Dashboard/v2/PillarShell'

export interface TabConfig<TabId extends string> {
  id: TabId
  label: string
  /** Numeric or string badge shown in mono on the right of the label. */
  badge?: string | number | null
}

interface Props<TabId extends string> {
  /** Ordered list of tabs to render. */
  tabs: ReadonlyArray<TabConfig<TabId>>
  /** Tab id used when the URL has no `?tab=…`. Elided from the URL. */
  defaultTab: TabId
  /** Pillar tone — drives the active-tab badge accent color. */
  tone: PillarTone
  /** Right-side actions slot (filter, new-scan, threshold buttons). Optional. */
  actions?: React.ReactNode
  /** URL search param name. Defaults to `tab` to match the existing pages. */
  paramKey?: string
  /** Optional aria-label for the tablist. Defaults to "Posture view". */
  ariaLabel?: string
}

/**
 * Hook variant of the URL sync — exposed so pages can both read the active
 * tab and drive their per-tab content rendering from a single source of
 * truth. Matches the shape of `useDashboardTab` in PostureTabs.tsx.
 */
export function usePillarTab<TabId extends string>(
  validTabs: ReadonlyArray<TabId>,
  defaultTab: TabId,
  paramKey: string = 'tab',
): [TabId, (next: TabId) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get(paramKey)

  // Validate against the page's own tab set so a stale or hand-crafted URL
  // can't put us into an unknown tab id. Falls back to the default.
  const current: TabId = useMemo(() => {
    if (raw && (validTabs as ReadonlyArray<string>).includes(raw)) return raw as TabId
    return defaultTab
  }, [raw, validTabs, defaultTab])

  const setTab = useCallback(
    (next: TabId) => {
      setParams(prev => {
        if (next === defaultTab) prev.delete(paramKey)
        else prev.set(paramKey, next)
        return prev
      }, { replace: true })
    },
    [defaultTab, paramKey, setParams],
  )

  return [current, setTab]
}

export default function PillarTabs<TabId extends string>({
  tabs,
  defaultTab,
  tone,
  actions,
  paramKey = 'tab',
  ariaLabel = 'Posture view',
}: Props<TabId>) {
  const validIds = useMemo(() => tabs.map(t => t.id), [tabs])
  const [current, setTab] = usePillarTab(validIds, defaultTab, paramKey)

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      data-tone={tone}
      className="
        sticky top-[64px] z-20 -mx-1 mb-9
        flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between
        border-b border-dark-700 bg-dark-900/95 px-1 py-4 backdrop-blur
        dark:bg-dark-900/95
      "
    >
      <div
        className="
          inline-flex items-center gap-0 self-start rounded-full border border-dark-600
          bg-dark-800 p-1
        "
      >
        {tabs.map(tab => {
          const active = tab.id === current
          // Badges render in mono so digit ranges line up with the rest of
          // the editorial pages (matches the `.badge-mini` mockup style).
          const badge =
            tab.badge != null && tab.badge !== '' ? String(tab.badge) : null

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={active}
              aria-controls={`panel-${tab.id}`}
              id={`tab-${tab.id}`}
              onClick={() => setTab(tab.id)}
              className={clsx(
                'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-200',
                active
                  ? 'bg-[#212938] text-white shadow-[0_4px_12px_rgba(33,41,56,0.18)]'
                  : 'text-slate-500 hover:text-slate-100 dark:hover:text-slate-100',
              )}
            >
              {tab.label}
              {badge && (
                <span
                  className={clsx(
                    'rounded-full px-1.5 py-0.5 font-mono text-[10px] font-bold tabular-nums',
                    active
                      ? 'text-[#212938]'
                      : 'bg-dark-700 text-slate-300',
                  )}
                  // Active badge background pulls the pillar tone via the
                  // `[data-tone]` accent map. Inactive falls through to the
                  // dark-700/slate Tailwind utilities above.
                  style={
                    active
                      ? { background: 'var(--tone-accent)' }
                      : undefined
                  }
                >
                  {badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {actions && (
        <div className="flex flex-wrap items-center gap-3">{actions}</div>
      )}
    </div>
  )
}
